import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Award, Quote, Shield, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const LAW_ICONS = {
  loi_terrain: '🏔️',
  gestion_munitions: '🎯',
  deposer_sac: '🎒',
  code_meute: '👥',
  execution_perfection: '⚡',
};

const FridayDebrief = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);

  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  const fetchDebrief = useCallback(async () => {
    try {
      const res = await api.get('/debrief/friday');
      if (res.data.debrief) {
        // Only show if generated this week
        const genDate = new Date(res.data.debrief.generated_at);
        const now = new Date();
        const daysDiff = (now - genDate) / (1000 * 60 * 60 * 24);
        if (daysDiff <= 3) {
          setDebrief(res.data.debrief);
        }
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDebrief(); }, [fetchDebrief]);

  if (loading || !debrief || dismissed) return null;

  const law = debrief.detected_law || {};
  const content = debrief.content || {};
  const stats = debrief.stats || {};
  const lawIcon = LAW_ICONS[law.id] || '⚔️';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="friday-debrief"
    >
      <Card className="border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/5 via-card to-orange-500/5 overflow-hidden">
        <CardContent className="p-0">
          {/* Header */}
          <div
            className="p-4 cursor-pointer flex items-center justify-between"
            onClick={() => setExpanded(!expanded)}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/30 to-orange-500/20 flex items-center justify-center">
                <Award className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="font-heading text-sm uppercase tracking-widest text-amber-400" data-testid="debrief-title">
                  {t('Debriefing de Victoire', 'Victory Debrief')}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t('Vendredi 17h — Rapport de la semaine', 'Friday 5PM — Weekly Report')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Mini stats */}
              <div className="hidden sm:flex items-center gap-3 text-[10px] text-muted-foreground mr-2">
                <span>{stats.meals_logged || 0} {t('repas', 'meals')}</span>
                <span>{stats.workouts_completed || 0} {t('seances', 'workouts')}</span>
                <span>{stats.active_days || 0}/5 {t('jours', 'days')}</span>
              </div>
              {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </div>
          </div>

          {/* Body */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4 space-y-4">
                  {/* 1. LAW IDENTIFICATION */}
                  <div className="p-3 rounded-xl border border-amber-500/20 bg-amber-500/5" data-testid="debrief-law">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{lawIcon}</span>
                      <p className="font-heading text-xs uppercase tracking-wider text-amber-300">
                        {law.name || t('Loi Appliquee', 'Applied Law')}
                      </p>
                    </div>
                    <p className="text-sm text-foreground/90 leading-relaxed">
                      {content.law_analysis || law.reason || ''}
                    </p>
                  </div>

                  {/* 2. TACTICAL QUOTE */}
                  <div className="p-3 rounded-xl border border-orange-500/20 bg-orange-500/5" data-testid="debrief-quote">
                    <div className="flex items-start gap-2">
                      <Quote className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-orange-300/70 font-heading uppercase tracking-wider mb-1">
                          {t('Citation Tactique', 'Tactical Quote')}
                        </p>
                        <p className="text-sm text-foreground italic leading-relaxed">
                          "{content.tactical_quote || t('Le repos est une arme. Utilise-la bien.', 'Rest is a weapon. Use it wisely.')}"
                        </p>
                        <p className="text-[10px] text-orange-400/60 mt-1 text-right">— YUMIX</p>
                      </div>
                    </div>
                  </div>

                  {/* 3. DISMISSAL ORDER */}
                  <div className="p-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5" data-testid="debrief-dismissal">
                    <div className="flex items-start gap-2">
                      <Shield className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-emerald-300/70 font-heading uppercase tracking-wider mb-1">
                          {t('Ordre de Dispersion', 'Dismissal Order')}
                        </p>
                        <p className="text-sm text-foreground/90 leading-relaxed">
                          {content.dismissal_order || t('Mission accomplie. Repose-toi. Le terrain t\'attend lundi.', 'Mission complete. Rest up. The terrain awaits Monday.')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats summary bar */}
                  <div className="flex items-center justify-between pt-1 text-[10px] text-muted-foreground">
                    <div className="flex items-center gap-4">
                      <span>{stats.meals_logged || 0} {t('repas logues', 'meals logged')}</span>
                      <span>{stats.workouts_completed || 0} {t('seances', 'workouts')}</span>
                      <span>{stats.water_logs || 0} {t('verres d\'eau', 'glasses of water')}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="debrief-dismiss"
                    >
                      {t('Fermer', 'Close')}
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default FridayDebrief;
